"use client"

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react'

// Type definitions for enhanced context
export type SheetType = 'login' | 'register' | 'setup' | 'invite-setup';

export interface InvitationData {
  email?: string;
  name?: string;
  inviteCode?: string;
}

export interface OpenSheetOptions {
  invitationData?: InvitationData;
  forceType?: boolean;
  /** Where to send the user after a successful sign-in, for callers outside the header.
   *  The header only learns a destination from a `redirect_to` query param, so a sheet
   *  opened straight from a button had no destination and left the user where they were. */
  redirectTo?: string;
}

interface AuthSheetContextType {
  // Existing API - preserved for backward compatibility
  isSheetOpen: boolean
  setIsSheetOpen: (open: boolean) => void
  avatarUrl: string
  setAvatarUrl: React.Dispatch<React.SetStateAction<string>>
  userName: string
  setUserName: React.Dispatch<React.SetStateAction<string>>
  userEmail: string
  setUserEmail: React.Dispatch<React.SetStateAction<string>>
  
  // Enhanced API - new properties
  sheetType: SheetType
  openSheet: (type?: SheetType, options?: OpenSheetOptions) => void
  closeSheet: () => void
  /** Post-sign-in destination requested by whoever opened the sheet, if any. */
  sheetRedirectTo: string | null
  invitationData: InvitationData | null
  setInvitationData: (data: InvitationData | null) => void
  
  // Session awareness
  isInviteContext: boolean
}

const AuthSheetContext = createContext<AuthSheetContextType | undefined>(undefined)

export function AuthSheetProvider({ children }: { children: React.ReactNode }) {
  // Existing state - preserved for backward compatibility
  const [isSheetOpen, setIsSheetOpenState] = useState(false)
  const [avatarUrl, setAvatarUrl] = useState<string>('')
  const [userName, setUserName] = useState<string>('')
  const [userEmail, setUserEmail] = useState<string>('')
  
  // Enhanced state
  const [sheetType, setSheetType] = useState<SheetType>('login')
  const [invitationData, setInvitationData] = useState<InvitationData | null>(null)
  const [sheetRedirectTo, setSheetRedirectTo] = useState<string | null>(null)
  
  // Derive invitation context from data only (route checking removed as unnecessary)
  const isInviteContext = useMemo(() => {
    return !!invitationData
  }, [invitationData])
  
  
  // Enhanced openSheet method - with smart logic but stable dependencies
  const openSheet = useCallback((type?: SheetType, options?: OpenSheetOptions) => {
    // Update invitation data first if provided
    if (options?.invitationData) {
      setInvitationData(options.invitationData)
    }
    setSheetRedirectTo(options?.redirectTo ?? null)
    
    // Smart type determination - upgrade login to invite-setup when invitation data is present
    let targetType: SheetType = type || 'login'
    
    // Auto-upgrade logic without external dependencies
    if (!options?.forceType) {
      // If we have invitation data (either from options or existing state), upgrade login to invite-setup
      const hasInvitationData = options?.invitationData || invitationData
      if (targetType === 'login' && hasInvitationData) {
        targetType = 'invite-setup'
      }
    }
    
    setSheetType(targetType)
    setIsSheetOpenState(true)
  }, [invitationData]) // Only depend on invitationData state
  
  // Enhanced closeSheet method
  const closeSheet = useCallback(() => {
    setIsSheetOpenState(false)
    // Don't clear sheetType or invitationData on close - preserve for reopening
  }, [])
  
  // Backward compatible setIsSheetOpen wrapper - simplified to break circular dependency
  const setIsSheetOpen = useCallback((open: boolean) => {
    if (open) {
      setSheetType('login') // Default type
      setIsSheetOpenState(true)
    } else {
      setIsSheetOpenState(false)
    }
  }, []) // No dependencies
  

  const value = {
    // Existing API - preserved for backward compatibility
    isSheetOpen,
    setIsSheetOpen,
    avatarUrl,
    setAvatarUrl,
    userName,
    setUserName,
    userEmail,
    setUserEmail,
    
    // Enhanced API
    sheetType,
    openSheet,
    closeSheet,
    sheetRedirectTo,
    invitationData,
    setInvitationData,
    
    // Session awareness
    isInviteContext,
  }

  return (
    <AuthSheetContext.Provider value={value}>
      {children}
    </AuthSheetContext.Provider>
  )
}

export function useAuthSheet(): AuthSheetContextType {
  const context = useContext(AuthSheetContext)
  if (context === undefined) {
    throw new Error('useAuthSheet must be used within an AuthSheetProvider')
  }
  return context
}
